import { phoneShapeToString, useInputDefinition } from '@gov-nx/utils/common';
import { usePPPLocale } from '../usePPPLocale';
import {
	type ContactDetailsFormData,
	type ContactDetailsFormFields,
	type PersonCommunicationData,
	type RezaBlockingMutationKey,
	useContactDetailsDefinition,
	useCountryCodes,
	usePersonCommunicationForm,
} from '@gov-nx/core/hooks';
import { type FormInputProps } from '@gov-nx/ui/types';
import {
	type FormDefinition2,
	useFormBuilderWithConsent,
} from '@gov-nx/core/service';
import { compose } from '@gov-nx/core/types';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import type { ComplaintDataRequest } from './service.types';
import { useMessageEvents } from '@gov-nx/core/events';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export interface Data extends ContactDetailsFormData {
	complaintDescription: string;
	complaintReason: string;
}

interface Fields extends ContactDetailsFormFields {
	complaintDescription: FormInputProps;
	complaintReason: FormInputProps;
}

export interface ComplaintFormDefinition {
	formDefinition: FormDefinition2<Data, Fields>;
	onSubmit: () => void;
	isSubmitting: boolean;
}

export const useComplaintForm = ({
	onFormValidated,
	pppId,
	email,
	phone,
	isLowAuthorization,
	serviceName,
	rezaBlockingMutationKey,
}: {
	onFormValidated: PersonCommunicationData['onFormValidated'];
	pppId?: string;
	email?: string;
	phone?: string;
	serviceName: string;
	rezaBlockingMutationKey: RezaBlockingMutationKey;
	isLowAuthorization: boolean;
}): ComplaintFormDefinition => {
	const { t } = usePPPLocale();
	const { axiosInstance } = useAxiosInstance();
	const { toastMessageSuccess } = useMessageEvents();

	const authContext = useAutorizaceContext();

	const mutation = usePoMutation({
		mutationKey: rezaBlockingMutationKey,
		mutationFn: compose(
			sendSubmissionQuery(axiosInstance),
			prepareSubmitData(pppId)
		),
		onSuccess: (data) => {
			const nonHighAuthorizationFlow = () => {
				formDefinition.formReset();
				toastMessageSuccess(t('reklamace.form.toast.success'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					serviceName,
					// @ts-expect-error - compose does return correct types
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const complaintDescription = useInputDefinition({
		isDisabled: mutation.isLoading,
		name: 'complaintDescription',
		isRequired: true,
		label: t('reklamace.form.complaintDescription.label'),
		placeholder: t('reklamace.form.complaintDescription.placeholder'),
		rows: 5,
	});

	const complaintReason = useInputDefinition({
		isDisabled: mutation.isLoading,
		name: 'complaintReason',
		label: t('reklamace.form.complaintReason.label'),
		placeholder: t('reklamace.form.complaintReason.placeholder'),
		rows: 5,
	});

	const contactDetailsDefinition = useContactDetailsDefinition({
		isDisabled: mutation.isLoading,
		strings: {
			_useEmail: { label: t('reklamace.form.email.label') },
			_usePhone: { label: t('reklamace.form.phone.label') },
			email: {
				label: '',
				placeholder: t('reklamace.form.email.placeholder'),
			},
			phone: {
				label: '',
				placeholder: t('reklamace.form.phone.placeholder'),
			},
		},
	});

	const formDefinition = useFormBuilderWithConsent<Data, Fields>(
		[complaintDescription, complaintReason, ...contactDetailsDefinition],
		{
			isDisabled: mutation.isLoading,
			isLowAuthorization,
		}
	);

	usePersonCommunicationForm(formDefinition, {
		countryCodes: useCountryCodes(),
		email,
		phone,
	});

	const onSubmit = formDefinition.formMethods.handleSubmit((data) => {
		mutation.mutate(data);
	}, onFormValidated);

	return {
		formDefinition,
		onSubmit,
		isSubmitting: mutation.isLoading,
	};
};

export const prepareSubmitData =
	(pppId?: string) =>
	(data: Data): ComplaintDataRequest => {
		if (pppId) {
			return {
				params: {},
				body: {
					kodPodani: 'PPP_REKLAMACE_ZAPSANE',
					pppId,
					typSubjektu: 'FO',
					kontaktEmail: data.email && data._useEmail ? data.email : undefined,
					kontaktTelefon:
						data.phone && data._usePhone
							? phoneShapeToString(data.phone)
							: undefined,
					popisReklamace: data.complaintDescription,
					duvodReklamace: data.complaintReason,
				},
			};
		}
		return {
			params: {},
			body: {
				kodPodani: 'PPP_REKLAMACE_NEZAPSANE',
				typSubjektu: 'FO',
				kontaktEmail: data.email && data._useEmail ? data.email : undefined,
				kontaktTelefon:
					data.phone && data._usePhone
						? phoneShapeToString(data.phone)
						: undefined,
				popisReklamace: data.complaintDescription,
				duvodReklamace: data.complaintReason,
			},
		};
	};
